import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Edit, Trash2, Eye } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { UserAvatar } from './UserAvatar';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const UserManagement = ({ user }) => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [managers, setManagers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingUserId, setEditingUserId] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: '',
    role: 'team_member',
    department_ids: [],
    manager_ids: [],
    team_lead_id: ''
  });
  const [teamLeads, setTeamLeads] = useState([]);

  useEffect(() => {
    fetchUsers();
    fetchDepartments();
    fetchManagers();
  }, []);

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data);
    } catch (error) {
      toast.error('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const fetchDepartments = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/departments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDepartments(response.data);
    } catch (error) {
      console.error('Failed to fetch departments');
    }
  };

  const fetchManagers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const managerUsers = response.data.filter(u => u.role === 'manager' || u.role === 'ceo');
      const teamLeadUsers = response.data.filter(u => u.role === 'team_lead');
      setManagers(managerUsers);
      setTeamLeads(teamLeadUsers);
    } catch (error) {
      console.error('Failed to fetch managers');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      
      // Prepare submit data
      const submitData = { ...formData };
      
      // If current user is a manager and creating/editing a team member or team lead, add current user as manager
      if (user.role === 'manager' && (formData.role === 'team_member' || formData.role === 'team_lead')) {
        // Add current manager's ID to manager_ids array
        if (!submitData.manager_ids.includes(user.id)) {
          submitData.manager_ids = [...submitData.manager_ids, user.id];
        }
      }
      
      // Convert empty team_lead_id to null
      if (submitData.team_lead_id === '') {
        submitData.team_lead_id = null;
      }
      
      if (isEditMode) {
        // Update existing user
        // Remove password if empty (don't update password)
        if (!submitData.password) {
          delete submitData.password;
        }
        // Remove email (can't change email)
        delete submitData.email;
        
        await axios.put(`${API_URL}/users/${editingUserId}`, submitData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('User updated successfully!');
      } else {
        // Create new user
        await axios.post(`${API_URL}/users`, submitData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('User added successfully. Email sent with credentials.');
      }
      
      setShowModal(false);
      resetForm();
      fetchUsers();
      fetchManagers(); // Refresh team leads list
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${isEditMode ? 'update' : 'add'} user`);
    }
  };

  const handleEdit = (userToEdit) => {
    setIsEditMode(true);
    setEditingUserId(userToEdit.id);
    setFormData({
      email: userToEdit.email,
      password: '', // Leave blank for edit
      full_name: userToEdit.full_name,
      role: userToEdit.role,
      department_ids: userToEdit.department_ids || [],
      manager_ids: userToEdit.manager_ids || [],
      team_lead_id: userToEdit.team_lead_id || ''
    });
    setShowModal(true);
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('User deleted successfully');
      fetchUsers();
    } catch (error) {
      toast.error('Failed to delete user');
    }
  };

  const resetForm = () => {
    setIsEditMode(false);
    setEditingUserId(null);
    setFormData({
      email: '',
      password: '',
      full_name: '',
      role: 'team_member',
      department_ids: [],
      manager_ids: [],
      team_lead_id: ''
    });
  };

  const getRoleOptions = () => {
    if (user.role === 'superadmin' || user.role === 'ceo') {
      return [
        { value: 'ceo', label: 'CEO' },
        { value: 'manager', label: 'Manager' },
        { value: 'team_lead', label: 'Team Lead' },
        { value: 'team_member', label: 'Team Member' },
        { value: 'educator', label: 'Educator' }
      ];
    }
    if (user.role === 'manager') {
      return [
        { value: 'team_lead', label: 'Team Lead' },
        { value: 'team_member', label: 'Team Member' }
      ];
    }
    return [{ value: 'team_member', label: 'Team Member' }];
  };

  if (loading) return <div>Loading...</div>;

  return (
    <div className="management-page" data-testid="user-management">
      <div className="page-header">
        <h2>User Management</h2>
        <button className="gradient-btn" onClick={() => setShowModal(true)} data-testid="add-user-btn">
          Add User
        </button>
      </div>

      <div className="table-container card">
        <table className="data-table" data-testid="users-table">
          <thead>
            <tr>
              <th>User</th>
              <th>Email</th>
              <th>Role</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} data-testid={`user-row-${u.id}`}>
                <td>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <UserAvatar 
                      userId={u.id} 
                      name={u.full_name} 
                      avatarUrl={u.avatar_url}
                      size={36}
                    />
                    <span style={{ fontWeight: '500' }}>{u.full_name}</span>
                  </div>
                </td>
                <td>{u.email}</td>
                <td className="role-badge">{u.role.replace('_', ' ')}</td>
                <td>
                  <span className={`status-badge ${u.is_active ? 'active' : 'inactive'}`}>
                    {u.is_active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button 
                      className="icon-btn" 
                      onClick={() => navigate(`/dashboard/profile/${u.id}`)} 
                      data-testid={`view-user-${u.id}`}
                      title="View profile"
                      style={{ background: 'rgba(99, 102, 241, 0.1)', color: '#6366F1' }}
                    >
                      <Eye size={18} />
                    </button>
                    <button 
                      className="icon-btn edit" 
                      onClick={() => handleEdit(u)} 
                      data-testid={`edit-user-${u.id}`}
                      title="Edit user"
                    >
                      <Edit size={18} />
                    </button>
                    <button 
                      className="icon-btn delete" 
                      onClick={() => handleDelete(u.id)} 
                      data-testid={`delete-user-${u.id}`}
                      title="Delete user"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div className="modal-overlay" data-testid="user-modal">
          <div className="modal-content large">
            <h3>{isEditMode ? 'Edit User' : 'Add New User'}</h3>
            <form onSubmit={handleSubmit} className="user-form">
              <div className="form-row">
                <div className="form-group">
                  <label>Full Name</label>
                  <input
                    type="text"
                    value={formData.full_name}
                    onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                    required
                    data-testid="user-fullname-input"
                  />
                </div>
                <div className="form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required={!isEditMode}
                    disabled={isEditMode}
                    data-testid="user-email-input"
                    style={isEditMode ? { background: '#f5f5f5', cursor: 'not-allowed' } : {}}
                  />
                  {isEditMode && (
                    <small style={{ color: '#888', fontSize: '12px' }}>Email cannot be changed</small>
                  )}
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Password {isEditMode && '(Leave blank to keep current)'}</label>
                  <input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required={!isEditMode}
                    placeholder={isEditMode ? 'Leave blank to keep current password' : ''}
                    data-testid="user-password-input"
                  />
                </div>
                <div className="form-group">
                  <label>Role</label>
                  <select
                    value={formData.role}
                    onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                    data-testid="user-role-select"
                  >
                    {getRoleOptions().map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              {formData.role === 'team_lead' && managers.length > 0 && (
                <div className="form-group">
                  <label>Assign to Manager(s) {user.role === 'manager' && '(You will be automatically assigned)'}</label>
                  {user.role !== 'manager' ? (
                    <select
                      multiple
                      value={formData.manager_ids}
                      onChange={(e) => {
                        const selected = Array.from(e.target.selectedOptions, option => option.value);
                        setFormData({ ...formData, manager_ids: selected });
                      }}
                      data-testid="user-manager-select"
                    >
                      {managers.map((m) => (
                        <option key={m.id} value={m.id}>{m.full_name}</option>
                      ))}
                    </select>
                  ) : (
                    <p style={{ fontSize: '14px', color: '#666', marginTop: '8px' }}>
                      You will be automatically assigned as the manager for this team lead.
                    </p>
                  )}
                </div>
              )}
              {formData.role === 'team_member' && (
                <>
                  {teamLeads.length > 0 && (
                    <div className="form-group">
                      <label>Assign to Team Lead (Optional)</label>
                      <select
                        value={formData.team_lead_id}
                        onChange={(e) => setFormData({ ...formData, team_lead_id: e.target.value })}
                        data-testid="user-teamlead-select"
                      >
                        <option value="">-- No Team Lead --</option>
                        {teamLeads.map((tl) => (
                          <option key={tl.id} value={tl.id}>{tl.full_name}</option>
                        ))}
                      </select>
                    </div>
                  )}
                  {managers.length > 0 && (
                    <div className="form-group">
                      <label>Assign to Manager(s) {user.role === 'manager' && '(You will be automatically assigned)'}</label>
                      {user.role !== 'manager' ? (
                        <select
                          multiple
                          value={formData.manager_ids}
                          onChange={(e) => {
                            const selected = Array.from(e.target.selectedOptions, option => option.value);
                            setFormData({ ...formData, manager_ids: selected });
                          }}
                          data-testid="user-manager-select"
                        >
                          {managers.map((m) => (
                            <option key={m.id} value={m.id}>{m.full_name}</option>
                          ))}
                        </select>
                      ) : (
                        <p style={{ fontSize: '14px', color: '#666', marginTop: '8px' }}>
                          You will be automatically assigned as the manager for this team member.
                        </p>
                      )}
                    </div>
                  )}
                </>
              )}
              <div className="modal-actions">
                <button type="button" onClick={() => { setShowModal(false); resetForm(); }} data-testid="cancel-user-btn">Cancel</button>
                <button type="submit" className="gradient-btn" data-testid="submit-user-btn">
                  {isEditMode ? 'Update User' : 'Add User'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;